/**
 * Newsletter RPC Schema Contracts
 *
 * Zod schemas for newsletter-related RPC responses:
 * - get_daily_newsletter_cases: Cases selected for daily newsletter
 * - get_active_subscribers: Active newsletter subscribers
 * - record_email_bounce: Bounce recording
 *
 * These validate the SQL functions that determine:
 * 1. Which cases go into newsletters (aangemaakt_op, portal, samenvatting, nieuwsbrief_eligible)
 * 2. Which subscribers receive them (newsletter_type, portal, active status)
 */

import { z } from 'zod';

// =============================================================================
// Newsletter Case Schema (get_daily_newsletter_cases)
// =============================================================================

export const NewsletterCaseSchema = z.object({
  id: z.string().min(1),
  ecli: z.string(),
  titel: z.string(),
  samenvatting: z.string().nullable(),
  uitspraak_datum: z.string(),
  publicatie_datum: z.string(),
  instantie_naam: z.string().nullable(),
  themas: z.array(z.string()),
  wetsartikelen: z.array(z.string()),
});

export type NewsletterCase = z.infer<typeof NewsletterCaseSchema>;

// =============================================================================
// Active Subscriber Schema (get_active_subscribers)
// =============================================================================

export const ActiveSubscriberSchema = z.object({
  email: z.string(),
  naam: z.string().nullable(),
  voorkeur_pdf_bijlage: z.boolean(),
});

export type ActiveSubscriber = z.infer<typeof ActiveSubscriberSchema>;

// =============================================================================
// Validation helpers
// =============================================================================

export function validateNewsletterCase(data: unknown): NewsletterCase {
  return NewsletterCaseSchema.parse(data);
}

export function safeParseNewsletterCase(data: unknown) {
  return NewsletterCaseSchema.safeParse(data);
}

export function validateActiveSubscriber(data: unknown): ActiveSubscriber {
  return ActiveSubscriberSchema.parse(data);
}

export function safeParseActiveSubscriber(data: unknown) {
  return ActiveSubscriberSchema.safeParse(data);
}
